<template>
  <Card class="chat-container">
    <template #title> ASISTENTE DE DATOS INTELIGENTE </template>
    <template #content>
      <div class="message-history" ref="messageHistory">
        <div
          v-for="msg in messages"
          :key="msg.id"
          :class="{
            'message-sent-wrapper': msg.type === 'sent',
            'message-received-wrapper': msg.type === 'received',
          }"
        >
          <template v-if="msg.type === 'received'">
            <Avatar label="G" shape="circle" class="message-avatar" />
            <div class="chat-bubble-received">
              <span class="message-text" v-html="msg.text"></span>
            </div>
          </template>
          <template v-else>
            <div class="chat-bubble-sent">
              <span class="message-text">{{ msg.text }}</span>
            </div>
            <Avatar
              :label="userInitial"
              shape="circle"
              class="message-avatar"
            />
          </template>
        </div>
        <div
          v-if="loading"
          style="margin: 40px 0 0 30px"
          class="flex justify-content-start align-items-center"
        >
          <div class="loading-dots">
            <i class="pi pi-circle-fill dot"></i>
            <i class="pi pi-circle-fill dot"></i>
            <i class="pi pi-circle-fill dot"></i>
          </div>
        </div>
      </div>
      <div class="message-input">
        <InputText
          v-model="newMessage"
          placeholder="Escribe tu mensaje..."
          @keyup.enter="sendMessage"
          class="w-full"
        />
        <Button
          label="Enviar"
          icon="pi pi-send"
          @click="sendMessage"
          :disabled="loading"
        />
      </div>
    </template>
  </Card>
</template>

<script>
import { ref, nextTick, onMounted } from "vue";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Card from "primevue/card";
import { useAuth } from "@/stores";
import ChatIAService from "@/service/chatIAService";

export default {
  components: {
    InputText,
    Button,
    Card,
  },
  data() {
    return {
      auth: useAuth(),
    };
  },
  computed: {
    userInitial() {
      return this.auth.user.name.charAt(0).toUpperCase();
    },
  },

  setup() {
    const messages = ref([
      {
        id: 1,
        text: `¡Hola! Soy tu asistente virtual inteligente.<br>Para asegurar que te brinde la información más precisa y relevante, te pido por favor que tus consultas sean **claras, específicas y directas**.<br> Estoy aquí para ayudarte a encontrar exactamente lo que necesitas de manera eficiente.`,
        type: "received",
        sender: "ChatBot",
      },
    ]);
    const newMessage = ref("");
    const messageHistory = ref(null);
    const loading = ref(false);
    const chatService = new ChatIAService();

    const sendMessage = () => {
      if (newMessage.value.trim() !== "") {
        const currentMessage = newMessage.value; // Guarda el mensaje antes de borrarlo
        messages.value.push({
          id: messages.value.length + 1,
          text: currentMessage,
          type: "sent",
          sender: "Yo",
        });
        loading.value = true;
        newMessage.value = ""; // Borra la entrada inmediatamente para mejor UX

        chatService
          .enviarMessage({ texto: currentMessage })
          .then((response) => {
            messages.value.push({
              id: messages.value.length + 1,
              text: response.respuesta,
              type: "received",
              sender: "ChatBot",
            });
          })
          .catch((error) => {
            console.error("Error al enviar mensaje:", error);
            // Agrega un mensaje de error al chat o una notificación toast
            messages.value.push({
              id: messages.value.length + 1,
              text: "Error: No se pudo enviar el mensaje. Intenta de nuevo.",
              type: "received", // O un tipo especial 'error'
              sender: "Sistema",
            });
          })
          .finally(() => {
            loading.value = false;
            nextTick(() => {
              if (messageHistory.value) {
                messageHistory.value.scrollTop =
                  messageHistory.value.scrollHeight;
              }
            });
          });
      }
    };

    onMounted(() => {
      nextTick(() => {
        if (messageHistory.value) {
          messageHistory.value.scrollTop = messageHistory.value.scrollHeight;
        }
      });
    });

    return {
      messages,
      newMessage,
      sendMessage,
      messageHistory,
      loading,
    };
  },
};
</script>

<style scoped>
.chat-container {
  max-width: 250vh;
  margin: 20px auto;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.message-history {
  height: 300px;
  overflow-y: auto;
  padding: 10px;
  background: linear-gradient(to bottom right, #f0fde3, #7dbbb8);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);
  margin-bottom: 10px;
}

/* Contenedores de los mensajes para alinear */
.message-sent-wrapper {
  display: flex;
  justify-content: flex-end;
  /* Alinear a la derecha */
  margin-bottom: 8px;
}

.message-received-wrapper {
  display: flex;
  justify-content: flex-start;
  /* Alinear a la izquierda */
  margin-bottom: 8px;
}

/* Estilo general de la burbuja de chat */
.chat-bubble-sent,
.chat-bubble-received {
  max-width: 80%;
  padding: 8px 12px;
  position: relative;
  word-wrap: break-word;
  /* Para que el texto largo se rompa */
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1);
}

/* Burbuja de mensaje enviado */
.chat-bubble-sent {
  background-color: #dcf8c6;
  /* Verde claro tipo WhatsApp */
  color: #333;
  margin-right: 5px;
  /* Espacio para la cola si la tuvieran */
  border-radius: 8px 0px 8px 8px;
}

/* Burbuja de mensaje recibido */
.chat-bubble-received {
  background-color: #ffffff;
  /* Blanco para mensajes recibidos */
  color: #333;
  margin-left: 5px;
  border-radius: 0px 8px 8px 8px;
}

/* La flecha para mensajes recibidos */
.chat-bubble-received::before {
  content: "";
  position: absolute;
  top: 0px;
  /* Ajusta según sea necesario para la posición vertical */
  left: -9px;
  /* Coloca la cola fuera del borde izquierdo */
  width: 0;
  height: 0;
  border-top: 10px solid #ffffff;
  border-left: 10px solid transparent;
  /* El color de la cola debe coincidir con el fondo de la burbuja */
  border-bottom: 10px solid transparent;
}

.chat-bubble-sent::before {
  content: "";
  position: absolute;
  top: 0px;
  /* Ajusta según sea necesario para la posición vertical */
  right: -9px;
  /* Coloca la cola fuera del borde derecho */
  width: 0;
  height: 0;
  border-top: 10px solid #dcf8c6;
  border-right: 10px solid transparent;
  /* El color de la cola debe coincidir con el fondo de la burbuja */
  border-bottom: 10px solid transparent;
}

.message-sender {
  font-weight: bold;
  color: #008069;
  /* Color similar al de WhatsApp para el nombre */
  display: block;
  /* Para que el nombre esté en una línea separada */
  margin-bottom: 2px;
}

.message-text {
  font-size: 0.95rem;
}

.message-input {
  display: flex;
  gap: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--surface-border);
}

.w-full {
  flex-grow: 1;
  /* Para que el input ocupe el espacio disponible */
}

/* ANIMACION DE CARGA */
.loading-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #ffffff;
  border-radius: 10px;
  padding: 6px 15px;
}

.dot {
  font-size: 1rem;
  margin: 0 7px;
  animation: bounce 1.7s infinite ease-in-out both;
}

.dot:nth-child(1) {
  animation-delay: -0.32s;
}

.dot:nth-child(2) {
  animation-delay: -0.16s;
}

.dot:nth-child(3) {
  animation-delay: 0s;
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }

  40% {
    transform: scale(1);
  }
}
</style>
